import { Injectable, inject } from "@angular/core";
import type { PhotoSubmissionDTO } from "@magpie/types";
import { TrpcService } from "./trpc.service";
import { PartyService } from "./party.service";

@Injectable({
	providedIn: "root",
})
export class GameUploadService {
	private readonly trpc = inject(TrpcService);
	private readonly party = inject(PartyService);

	async requestPresignedUrl(
		sessionId: string,
		roundId: string,
		itemId: string,
		blob: Blob,
	): Promise<{ uploadUrl: string; objectKey: string }> {
		const { uploadUrl, objectKey } =
			await this.trpc.client.gameUpload.requestPhotoUpload.mutate({
				sessionId,
				roundId,
				itemId,
				filename: `photo-${Date.now()}.jpg`,
				contentType: "image/jpeg",
				sizeBytes: blob.size,
			});
		return { uploadUrl, objectKey };
	}

	async putToR2(uploadUrl: string, blob: Blob): Promise<void> {
		const maxAttempts = 3;
		let lastError: unknown;
		for (let attempt = 0; attempt < maxAttempts; attempt++) {
			try {
				const res = await fetch(uploadUrl, {
					method: "PUT",
					headers: { "Content-Type": "image/jpeg" },
					body: blob,
					signal: AbortSignal.timeout(30_000),
				});
				if (res.ok) return;
				// 4xx (signed-URL expired, bad request) — don't retry, caller re-signs
				if (res.status >= 400 && res.status < 500) {
					throw new Error(`R2 PUT failed: ${res.status} ${res.statusText}`);
				}
				lastError = new Error(`R2 PUT failed: ${res.status} ${res.statusText}`);
			} catch (err) {
				lastError = err;
			}
			if (attempt < maxAttempts - 1) {
				await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
			}
		}
		throw lastError;
	}

	async recordSubmission(
		sessionId: string,
		roundId: string,
		itemId: string,
		photoKey: string,
	): Promise<PhotoSubmissionDTO> {
		return await this.trpc.client.gameUpload.recordSubmission.mutate({
			sessionId,
			roundId,
			itemId,
			photoKey,
		});
	}

	broadcast(
		submissionId: string,
		userId: string,
		roundId: string,
		itemId: string,
		photoKey: string,
	): void {
		this.party.send({
			type: "submit_photo",
			submissionId,
			userId,
			roundId,
			itemId,
			photoKey,
		});
	}
}
